/**
 * Automations API contract (BAL-8506 / BAL-8361 Wave 0) — contract-first, so the Wave-1 FE tracks can
 * build against mocks before the routes exist.
 *
 * TWO mirrors, one vocabulary. **TypeBox** is the source of truth for the Wave-1 unified-api routes
 * (registers with Fastify, types the responses) — same reasoning as `workforce-agents.ts`. **Zod** is
 * the FE-forms mirror (backoffice TanStack Form + Zod). Both source every string domain from the same
 * `consts` arrays (report types, triggers, condition field keys, actions, capability/skill keys), and a
 * parity contract test asserts the two shapes can't drift. No route handlers here — schemas only.
 *
 * The condition wire shape is the builder state from the condition grammar (slice C) OR the `advanced`
 * marker for a hand-authored JsonLogic rule the grammar can't round-trip. Task definitions carry
 * capability/skill KEYS, never raw tools — the server derives the closed toolset (slice E).
 */

import type { Static } from '@sinclair/typebox';
import { Type } from '@sinclair/typebox';
import { z } from 'zod';

import { WORKFORCE_AGENT_SKILL_KEYS } from '../consts/workforce-agents';
import { WORKFORCE_CAPABILITY_KEYS } from '../consts/workforce-capabilities';
import {
  WORKFORCE_AUTOMATION_REPORT_TYPES,
  WORKFORCE_AUTOMATION_TRIGGERS,
  WORKFORCE_CONDITION_FIELD_KEYS,
} from '../consts/workforce-condition-fields';
import {
  WORKFORCE_AUTOMATION_TASK_MODES,
  WORKFORCE_ON_CLEAR_ACTIONS,
  WORKFORCE_ON_FLAG_ACTIONS,
  WORKFORCE_SUMMARY_LENGTHS,
} from '../consts/workforce-task-automation';

// TypeBox string-literal-union over a const array (the `T.StringEnum` equivalent; `T` isn't in common).
const TEnum = <T extends string>(values: readonly T[]) =>
  Type.Union(values.map(value => Type.Literal(value)));

const CONDITION_COMBINATORS = ['and', 'or'] as const;

// ============================================================================
// Condition wire shape (TypeBox) — the builder state, or the advanced marker.
// ============================================================================

export const AutomationConditionRowSchema = Type.Object({
  field: TEnum(WORKFORCE_CONDITION_FIELD_KEYS),
  // minItems 1 — an empty selection compiles to an always-false leaf (scalar-in) or silently flips a
  // presence field to `false`; both are rules that never behave as authored. Review-fix (2026-08-16).
  // maxItems 1000 — a curated allowlist is a legitimate rule shape (the MCC catalog alone is 982
  // entries); still bounds a hostile payload. Review-fix (2026-08-16).
  values: Type.Array(Type.String(), { minItems: 1, maxItems: 1000 }),
});

export const AutomationConditionGroupSchema = Type.Object({
  combinator: TEnum(CONDITION_COMBINATORS),
  // rows minItems 1 — a zero-row group is builder junk that compiles to `true` (an UNCONDITIONAL
  // automation firing on every report); reject it at the contract. maxItems 10 — structure is preserved
  // (no DNF expansion), so the old cartesian-explosion guard is gone; this just bounds a hostile
  // payload. Generous vs the UI. Review-fix (2026-08-16).
  rows: Type.Array(AutomationConditionRowSchema, { minItems: 1, maxItems: 10 }),
});

export const AutomationConditionStateSchema = Type.Object({
  combinator: TEnum(CONDITION_COMBINATORS),
  // groups intentionally has NO minItems: `groups: []` IS the ratified "no condition — fires on every
  // matching report" state (compiles to `true`; see emptyConditionState). Do NOT add minItems here.
  // maxItems 10 bounds a hostile payload. Review-fix (2026-08-16).
  groups: Type.Array(AutomationConditionGroupSchema, { maxItems: 10 }),
});

/** A seeded / hand-authored rule the builder grammar can't round-trip — rendered read-only. */
export const AutomationAdvancedConditionSchema = Type.Object({
  advanced: Type.Literal(true),
  raw: Type.Unknown(),
});

export const AutomationConditionSchema = Type.Union([
  AutomationConditionStateSchema,
  AutomationAdvancedConditionSchema,
]);
export type TAutomationCondition = Static<typeof AutomationConditionSchema>;

// ============================================================================
// Task definition (TypeBox) — the automation's single task (v1). Carries capability/skill KEYS,
// never raw tools; the server derives the closed toolset + maxSteps.
// ============================================================================

export const AutomationTaskDefinitionSchema = Type.Object({
  // Who does the work. `manual` ⇒ no agent_config: capabilities/skills must be empty and the
  // decision/summary parts are ignored (route-enforced in Wave 1). Review-fix (2026-08-16).
  mode: TEnum(WORKFORCE_AUTOMATION_TASK_MODES),
  // Free-text bounds — a label vs long-form prompts. Bounds a hostile payload. Review-fix (2026-08-16).
  name: Type.String({ maxLength: 256 }),
  instructions: Type.String({ maxLength: 10000 }),
  decisionFlagWhen: Type.String({ maxLength: 10000 }),
  decisionClearWhen: Type.String({ maxLength: 10000 }),
  onFlagAction: TEnum(WORKFORCE_ON_FLAG_ACTIONS),
  onClearAction: TEnum(WORKFORCE_ON_CLEAR_ACTIONS),
  summaryGuidance: Type.String({ maxLength: 10000 }),
  summaryLength: TEnum(WORKFORCE_SUMMARY_LENGTHS),
  capabilities: Type.Array(TEnum(WORKFORCE_CAPABILITY_KEYS)),
  skills: Type.Array(TEnum(WORKFORCE_AGENT_SKILL_KEYS)),
  ownerId: Type.Union([Type.String(), Type.Null()]),
});
export type TAutomationTaskDefinition = Static<typeof AutomationTaskDefinitionSchema>;

// ============================================================================
// Read models (TypeBox) — list item + full resource.
// ============================================================================

export const AutomationListItemSchema = Type.Object({
  id: Type.String(), // the rule id
  name: Type.String(),
  trigger: TEnum(WORKFORCE_AUTOMATION_TRIGGERS),
  reportTypes: Type.Array(TEnum(WORKFORCE_AUTOMATION_REPORT_TYPES)),
  conditionSummary: Type.String(),
  enabled: Type.Boolean(),
  firedCount: Type.Number(),
  lastFiredAt: Type.Union([Type.String(), Type.Null()]),
  capabilities: Type.Array(TEnum(WORKFORCE_CAPABILITY_KEYS)),
  skills: Type.Array(TEnum(WORKFORCE_AGENT_SKILL_KEYS)),
});
export type TAutomationListItem = Static<typeof AutomationListItemSchema>;

export const AutomationResourceSchema = Type.Object({
  id: Type.String(),
  name: Type.String(),
  trigger: TEnum(WORKFORCE_AUTOMATION_TRIGGERS),
  reportTypes: Type.Array(TEnum(WORKFORCE_AUTOMATION_REPORT_TYPES)),
  conditionSummary: Type.String(),
  enabled: Type.Boolean(),
  firedCount: Type.Number(),
  lastFiredAt: Type.Union([Type.String(), Type.Null()]),
  capabilities: Type.Array(TEnum(WORKFORCE_CAPABILITY_KEYS)),
  skills: Type.Array(TEnum(WORKFORCE_AGENT_SKILL_KEYS)),
  condition: AutomationConditionSchema,
  // A `tasks` ARRAY (exactly one in v1) mirroring `WorkforceEmit.tasks`, so multi-def emits later are a
  // validation relaxation, not a reshape.
  tasks: Type.Array(AutomationTaskDefinitionSchema, { minItems: 1, maxItems: 1 }),
});
export type TAutomationResource = Static<typeof AutomationResourceSchema>;

// ============================================================================
// Write models (TypeBox) — create body + toggle + list query.
// ============================================================================

export const CreateAutomationBodySchema = Type.Object({
  trigger: TEnum(WORKFORCE_AUTOMATION_TRIGGERS),
  // minItems 1 — a rule with no report-type selector would never fire. Review-fix (2026-08-16).
  reportTypes: Type.Array(TEnum(WORKFORCE_AUTOMATION_REPORT_TYPES), { minItems: 1 }),
  condition: AutomationConditionStateSchema,
  tasks: Type.Array(AutomationTaskDefinitionSchema, { minItems: 1, maxItems: 1 }),
});
export type TCreateAutomationBody = Static<typeof CreateAutomationBodySchema>;

export const ToggleAutomationBodySchema = Type.Object({ enabled: Type.Boolean() });
export type TToggleAutomationBody = Static<typeof ToggleAutomationBodySchema>;

export const ListAutomationsQuerySchema = Type.Object({
  search: Type.Optional(Type.String({ maxLength: 256 })),
});
export type TListAutomationsQuery = Static<typeof ListAutomationsQuerySchema>;

// ============================================================================
// Zod mirrors — the FE-forms surface. Same field sets as the TypeBox schemas above (asserted by the
// parity contract test); string domains sourced from the same consts.
// ============================================================================

const zEnum = <T extends string>(values: readonly T[]) => z.enum(values as unknown as [T, ...T[]]);

export const AutomationConditionRowZodSchema = z.object({
  field: zEnum(WORKFORCE_CONDITION_FIELD_KEYS),
  // max 1000 — a curated allowlist is legitimate (MCC catalog alone is 982). Mirrors TypeBox.
  values: z.array(z.string()).min(1).max(1000),
});

export const AutomationConditionGroupZodSchema = z.object({
  combinator: zEnum(CONDITION_COMBINATORS),
  // .min(1) — a zero-row group compiles to `true` (unconditional automation); reject it. Mirrors TypeBox.
  rows: z.array(AutomationConditionRowZodSchema).min(1).max(10),
});

export const AutomationConditionStateZodSchema = z.object({
  combinator: zEnum(CONDITION_COMBINATORS),
  // NO .min() on groups: `groups: []` is the ratified no-condition (fires on every report) state.
  groups: z.array(AutomationConditionGroupZodSchema).max(10),
});

export const AutomationAdvancedConditionZodSchema = z.object({
  advanced: z.literal(true),
  raw: z.unknown(),
});

export const AutomationConditionZodSchema = z.union([
  AutomationConditionStateZodSchema,
  AutomationAdvancedConditionZodSchema,
]);

export const AutomationTaskDefinitionZodSchema = z.object({
  mode: zEnum(WORKFORCE_AUTOMATION_TASK_MODES),
  name: z.string().max(256),
  instructions: z.string().max(10000),
  decisionFlagWhen: z.string().max(10000),
  decisionClearWhen: z.string().max(10000),
  onFlagAction: zEnum(WORKFORCE_ON_FLAG_ACTIONS),
  onClearAction: zEnum(WORKFORCE_ON_CLEAR_ACTIONS),
  summaryGuidance: z.string().max(10000),
  summaryLength: zEnum(WORKFORCE_SUMMARY_LENGTHS),
  capabilities: z.array(zEnum(WORKFORCE_CAPABILITY_KEYS)),
  skills: z.array(zEnum(WORKFORCE_AGENT_SKILL_KEYS)),
  ownerId: z.string().nullable(),
});

export const AutomationListItemZodSchema = z.object({
  id: z.string(),
  name: z.string(),
  trigger: zEnum(WORKFORCE_AUTOMATION_TRIGGERS),
  reportTypes: z.array(zEnum(WORKFORCE_AUTOMATION_REPORT_TYPES)),
  conditionSummary: z.string(),
  enabled: z.boolean(),
  firedCount: z.number(),
  lastFiredAt: z.string().nullable(),
  capabilities: z.array(zEnum(WORKFORCE_CAPABILITY_KEYS)),
  skills: z.array(zEnum(WORKFORCE_AGENT_SKILL_KEYS)),
});

export const AutomationResourceZodSchema = z.object({
  id: z.string(),
  name: z.string(),
  trigger: zEnum(WORKFORCE_AUTOMATION_TRIGGERS),
  reportTypes: z.array(zEnum(WORKFORCE_AUTOMATION_REPORT_TYPES)),
  conditionSummary: z.string(),
  enabled: z.boolean(),
  firedCount: z.number(),
  lastFiredAt: z.string().nullable(),
  capabilities: z.array(zEnum(WORKFORCE_CAPABILITY_KEYS)),
  skills: z.array(zEnum(WORKFORCE_AGENT_SKILL_KEYS)),
  condition: AutomationConditionZodSchema,
  tasks: z.array(AutomationTaskDefinitionZodSchema).min(1).max(1),
});

export const CreateAutomationBodyZodSchema = z.object({
  trigger: zEnum(WORKFORCE_AUTOMATION_TRIGGERS),
  reportTypes: z.array(zEnum(WORKFORCE_AUTOMATION_REPORT_TYPES)).min(1),
  condition: AutomationConditionStateZodSchema,
  tasks: z.array(AutomationTaskDefinitionZodSchema).min(1).max(1),
});

export const ToggleAutomationBodyZodSchema = z.object({ enabled: z.boolean() });

export const ListAutomationsQueryZodSchema = z.object({
  search: z.string().max(256).optional(),
});
